<template>
  <div>
    <page-header :actions="pageActionConfig" :page="pageConfig" />
    <add-consultation
      v-if="showCardConsultation"
      title="Añadir Consulta"
      @on-close="showCardConsultation = false"
    />
    <request-reinbursement
      v-if="showRequest"
      :filter="filterModel"
      :request-type="disbursementRequestType"
      @on-cancel="disbursementRequestType = 0"
    />
    <filter-manager
      v-if="showFilterManager"
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      :open-advanced="showAdvanced"
      @academicYear="onChangeAcademicYearHandler"
      @center="onChangeCenterHandler"
      @determination="onChangeDeterminationHandler"
      @isOneTimePayment="onChangeIsOneTimePaymentHandler"
      @month="onChangeMonthHandler"
      @municipality="onChangeMunicipalityHandler"
      @requestStatus="onChangeRequestStatusHandler"
      @school="onChangeSchoolHandler"
      @search="onSearchHandler"
    />
    <div v-if="showProcessDirectPayment" class="payment-process-section">
      <payment-process
        :academic-year-id="filterModel.academicYear.id"
        :items="checkedRows"
        :month="filterModel.month.id"
        :year="filterModel.academicYear.year"
        @cancel="cancelPaymentProcess"
        @saved="savedPaymentProcess"
      />
      <check-box
        class="d-flex justify-content-end mt-4 pr-2"
        :class="[{ checked: allRowsChecked }]"
        text="Seleccionar todo"
        :value="allRowsChecked"
        @input="onCheckAllRowsHandler"
      />
    </div>

    <custom-separator class="my-4" text="Resultados" />
    <custom-result-not-found
      v-if="results.length == 0"
      text="No hay información disponible."
    />
    <div v-for="row of results" :key="row.id">
      <result-row :checkable="showProcessDirectPayment" :data="row" />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow.vue';
import AddConsultation from './components/AddConsultation.vue';
import { getReportRefundHandling as _getReport } from '@/modules/shared/loadingprocessreport.api.js';
import disbursementRequestType from '@/utils/constants/disbursementRequestType';
import disbursementRequestStatus from '@/utils/constants/disbursementRequestStatus';

import schoolTypes from '@/utils/constants/schoolTypes';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

import {
  getInitFilters as _getInitFilters,
  getCenters as _getCenters,
  getDisbursementRequests as _getDisbursementRequests,
  getVisitCertificationDirectPayment as _getVisitCertificationDirectPayment,
} from '@/modules/reinbursement/api.js';
import RequestReinbursement from './components/RequestReinbursement.vue';
import PaymentProcess from './components/PaymentProcess.vue';
import CheckBox from 'CheckBox';

import {
  getMunicipalities as _getMunicipalities,
  getSchools as _getSchools,
} from '@/modules/shared/location.api';

import { getAvailableMonths as _getAvailableMonths } from '@/modules/shared/academicyear.api.js';

import appServices from '@/modules/app/api.js';
import storeMixin from '@/shared/mixins/storeGenerator';

//constants
const DEFAULT_PAGE_SIZE = 20;
const MODULE_NAME = 'ReinbursementModule';
const MECHANISM_ELECTRONIC = 1;
const MECHANISM_HANDWRITTEN = 2;
const MECHANISM_OPTIONS = [
  { id: MECHANISM_HANDWRITTEN, name: 'Manuscrito' },
  { id: MECHANISM_ELECTRONIC, name: 'Digital' },
];
import { mapGetters } from 'vuex';
import roles from '@/utils/constants/roles';

export default {
  name: 'ReinbursementMain',
  components: {
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    CheckBox,
    AddConsultation,
    ResultRow,
    RequestReinbursement,
    PaymentProcess,
  },
  mixins: [storeMixin, alertMixin],

  data() {
    return {
      moduleName: MODULE_NAME,
      filterOptions: {
        academicYears: [],
        months: [],
        statuses: [],
        centers: [],
        municipalities: [],
        schools: [],
      },
      results: [],
      filterModel: {
        academicYear: null,
        month: null,
        requestStatus: null,
        requestNumberOrStudentNameOrSie: null,
        center: null,
        municipality: null,
        school: null,
        tapRequestNumber: null,
        determination: null,
        mechanism: null,
        isOneTimePayment: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      showCardConsultation: false,
      showValidation: false,
      disbursementRequestType: disbursementRequestType.UnSet,
      isSearchValid: false,
      validateVisitCertification: false,
      showAdvanced: this.institutionIsRequired,
    };
  },
  computed: {
    ...mapGetters('$_user', ['fullProfile', 'companies']),
    canRequest() {
      return this.userHasPermissions('provider.disbursement.addrequest');
    },
    canSearchConsultation() {
      return this.userHasPermissions('provider.consultation.search');
    },
    pageConfig() {
      return {
        icon: 'fas fa-user-circle',
        title: 'Manejo de Reembolso y Pago Directo',
        routes: ['Proveedor', 'Manejo de Reembolso y Pago Directo'],
      };
    },
    showRequest() {
      return this.showRequestDirectPayment || this.showRequestReinbursement;
    },
    showRequestDirectPayment() {
      return (
        this.disbursementRequestType === disbursementRequestType.DirectPayment
      );
    },
    showProcessDirectPayment() {
      const hasPermissions = this.userHasPermissions(
        'provider.disbursement.processdirectpayment'
      );
      if (!hasPermissions) return false;

      const filters = this.filterModel;
      const hasFilters =
        Boolean(filters.academicYear) &&
        Boolean(filters.month) &&
        Boolean(filters.school);
      if (!hasFilters) return false;

      const isApproved =
        filters.requestStatus?.id === disbursementRequestStatus.Aprobado;
      if (!isApproved) return false;

      const isDirectPayment =
        filters.determination?.id === disbursementRequestType.DirectPayment;
      if (!isDirectPayment) return false;

      return this.isSearchValid;
    },
    showRequestReinbursement() {
      return this.disbursementRequestType === disbursementRequestType.Refund;
    },
    showFilterManager() {
      return !this.showCardConsultation && !this.showRequest;
    },
    hideAllButtons() {
      return this.showCardConsultation || this.showRequest;
    },
    pageActionConfig() {
      return [
        {
          text: 'Certificación de Visita',
          icon: 'fas fa-download',
          variant: 'success',
          click: () => this.viewCertification(),
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print',
          variant: 'success',
          visible:
            this.hasPrintPermission &&
            !this.showCardConsultation &&
            !this.showRequest,
          click: () => this.onDownloadRefundHandling(),
        },
        {
          text: 'Solicitar Reembolso',
          icon: 'fas fa-money-check-alt',
          variant: 'success',
          visible: !this.hideAllButtons && this.showAddReinbursement,
          click: () =>
            this.openRequestReinbursement(disbursementRequestType.Refund),
        },
        {
          text: 'Solicitar Pago Directo',
          icon: 'fas fa-money-check-alt',
          variant: 'outline-success',
          visible:
            !this.hideAllButtons &&
            this.hasPermissionAddDirectPayment &&
            this.showAddDirectPayment,
          click: () =>
            this.openRequestReinbursement(
              disbursementRequestType.DirectPayment
            ),
        },
      ];
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    hasPersistInfo() {
      return Boolean(this.moduleData?.ReinbursementMain || false);
    },
    hasPrintPermission() {
      return this.userHasPermissions('provider.disbursement.process.download');
    },
    hasPermissionAddDirectPayment() {
      return this.userHasPermissions('provider.disbursement.adddirectpayment');
    },
    enableValidationMessage() {
      return this.showValidation ? 'required' : null;
    },
    visitCertificationFilterRequired() {
      return this.validateVisitCertification ? 'required' : null;
    },
    showAddReinbursement() {
      return this.userHasPermissions(
        'provider.disbursement.adddisbursementrequest'
      );
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          validations:
            this.visitCertificationFilterRequired ||
            this.enableValidationMessage,
          error: ' Favor seleccionar el año.',
          clearable: false,
        },
        {
          name: 'month',
          component: 'CustomDropDown',
          options: this.filterOptions.months,
          placeholder: 'Mes',
          fieldName: 'name',
          fieldKey: 'id',
          validations:
            this.visitCertificationFilterRequired ||
            this.enableValidationMessage,
          error: 'Campo requerido.',
          class: 'col-12 col-md-2',
        },
        {
          name: 'requestStatus',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
        },
        {
          name: 'requestNumberOrStudentNameOrSie',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          placeholder:
            'Escriba número de solicitud, nombre del estudiante o SIE',
          class: 'col-12 col-md-6',
        },
        {
          name: 'determination',
          component: 'CustomDropDown',
          options: this.filterOptions.requestTypes,
          label: 'Determinación:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.hasCompany ? 'col-12 col-md-3' : 'col-12 col-md-2',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.filterOptions.centers,
          label: 'Centro:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          clearable: true,
          isAdvanced: true,
          visible: !this.hasCompany,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filterOptions.municipalities,
          label: 'Municipio:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          clearable: true,
          isAdvanced: true,
          visible: !this.hasCompany,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.filterOptions.schools,
          label: 'Institución:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.hasCompany ? 'col-12 col-md-3' : 'col-12 col-md-2',
          clearable: true,
          isAdvanced: true,
          validations:
            this.visitCertificationFilterRequired || this.institutionIsRequired
              ? 'required'
              : null,
          error: 'La institución es requerida.',
        },
        {
          name: 'mechanism',
          component: 'CustomDropDown',
          options: MECHANISM_OPTIONS,
          label: 'Tipo de Firma:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.hasCompany ? 'col-12 col-md-3' : 'col-12 col-md-2',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'isOneTimePayment',
          component: 'CustomDropDown',
          options: [
            { id: true, name: 'Sí' },
            { id: false, name: 'No' },
          ],
          label: 'One Time Payment:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'tapRequestNumber',
          component: 'CustomInput',
          label: 'Escriba Número de Solicitud de la Consulta:',
          placeholder: 'Escriba Número de Solicitud de la Consulta',
          class: this.hasCompany ? 'col-12 col-md-3' : 'col-12 col-md-2',
          isAdvanced: true,
        },
      ];
    },
    checkedRows() {
      return this.results
        .filter((x) => x.checked)
        .map(({ requestId, amountToRefund }) => ({
          requestId,
          amountToRefund,
        }));
    },
    allRowsChecked() {
      return this.results.every((x) => x.checked);
    },
    showAddDirectPayment() {
      return (
        (Boolean(this.filterModel.determination?.id) &&
          this.filterModel.determination?.id ==
            disbursementRequestType.DirectPayment &&
          Boolean(this.filterModel.academicYear?.id) &&
          Boolean(this.filterModel.school?.id)) ||
        false
      );
    },
    hasCompany() {
      return this.fullProfile.companyid;
    },
    institutionIsRequired() {
      return [roles.ProviderPagoDirecto].includes(this.userRole);
    },
  },
  async mounted() {
    // Vuex and session storage can restore results from an older user scope.
    this.results = [];
    this.isSearchValid = false;
    this.resetPagination();
    if (!this.hasPersistInfo) {
      await this.setInitFilters();
    } else {
      const centerChanged = await this.refreshCenters();
      if (centerChanged && this.filterModel.academicYear) {
        await this.onChangeCenterHandler();
      }
    }
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async setInitFilters() {
      const { data } = await _getInitFilters();

      this.filterOptions = {
        ...data,
        academicYears: _.orderBy(data.academicYears, ['id'], ['desc']),
      };

      this.filterModel.academicYear = this.filterOptions.academicYears.find(
        (x) => x.selected
      );

      this.filterModel.determination = this.filterOptions.requestTypes.find(
        (x) => x.selected
      );

      this.filterModel.school = this.filterOptions?.schools?.find(
        (x) => x.selected
      );

      await this.onChangeAcademicYearHandler();
    },
    async onChangeAcademicYearHandler() {
      this.isSearchValid = false;
      if (this.filterModel.academicYear != null) {
        const { data } = await _getAvailableMonths(
          this.filterModel.academicYear.id
        );

        this.filterModel.month = null;
        this.filterOptions = {
          ...this.filterOptions,
          months: data,
        };

        await this.refreshCenters();
        await this.onChangeCenterHandler();
      }
    },
    async refreshCenters() {
      const previousCenterId = this.filterModel.center?.id;
      this.filterOptions = { ...this.filterOptions, centers: [] };
      let userCenters;
      try {
        const { data } = await _getCenters();
        userCenters = data;
      } catch (error) {
        this.filterModel.center = null;
        this.filterModel.municipality = null;
        this.filterModel.school = null;
        this.filterOptions.municipalities = [];
        this.filterOptions.schools = [];
        throw error;
      }

      this.filterOptions = { ...this.filterOptions, centers: userCenters };
      this.filterModel.center =
        userCenters.length === 1
          ? { ...userCenters[0], disabled: true }
          : userCenters.find((center) => center.id === previousCenterId) ||
            null;

      const centerChanged = previousCenterId !== this.filterModel.center?.id;
      if (centerChanged) {
        this.filterModel.municipality = null;
        this.filterModel.school = null;
        this.filterOptions.municipalities = [];
        this.filterOptions.schools = [];
        this.results = [];
        this.isSearchValid = false;
        this.resetPagination();
      }
      return centerChanged;
    },
    async onChangeCenterHandler() {
      this.filterModel.municipality = null;
      this.filterModel.school = null;
      if (this.filterModel.center) {
        const { data } = await _getMunicipalities({
          academicYearId: this.filterModel.academicYear.id,
          billingCenterId: this.filterModel.center.id,
        });

        this.filterOptions = {
          ...this.filterOptions,
          municipalities: data,
        };

        if (data?.length === 1) {
          this.filterModel.municipality = {
            ...data[0],
            disabled: true,
          };
        }
      } else {
        this.filterOptions.municipalities = [];
        this.filterOptions.schools = [];
      }
      await this.onChangeMunicipalityHandler();
    },
    async onChangeMunicipalityHandler() {
      this.filterModel.school = null;
      let params = {
        academicYearId: this.filterModel.academicYear.id,
        schoolTypes: [schoolTypes.Institutions],
      };
      if (this.filterModel.center)
        params.billingCenterId = this.filterModel.center.id;

      if (this.filterModel.municipality)
        params.municipalityId = this.filterModel.municipality.id;

      const { data } = await _getSchools(params);

      this.filterOptions = {
        ...this.filterOptions,
        schools: data,
      };

      if (data?.length === 1) {
        this.filterModel.school = {
          ...data[0],
          disabled: true,
        };
      }
    },
    async onSearchHandler() {
      this.cancelPaymentProcess();
      if (!this.showAdvanced) {
        this.showAdvanced = this.institutionIsRequired;
        // Delay for show advanced filter
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      this.validateVisitCertification = false;
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) return;
      this.pagination.currentPage = 1;
      await this.searchRequests();
      this.isSearchValid = Boolean(this.results.length);
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      await this.searchRequests();
    },
    async onDownloadRefundHandling() {
      this.showValidation = false;
      await this.$refs.filterRef.$refs.formValidator.reset();
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();

      if (!isValid) return;
      await _getReport({
        AcademicYearId: this.filterModel.academicYear.id,
      }).then((res) => appServices.downloadReportByDocumentFile(res.data));
    },
    async searchRequests() {
      this.showValidation = false;
      const payload = {
        academicYearId: this.filterModel.academicYear.id,
        month: this.filterModel.month?.id,
        requestStatusId: this.filterModel.requestStatus?.id,
        requestNumberOrStudentNameOrSie: this.filterModel
          .requestNumberOrStudentNameOrSie,
        centerId: this.filterModel.center?.id,
        municipalityId: this.filterModel.municipality?.id,
        schoolId: this.filterModel.school?.id,
        tapRequestNumber: this.filterModel.tapRequestNumber,
        requestTypeId: this.filterModel.determination?.id,
        isElectronicSignature: this.filterModel.mechanism
          ? this.filterModel.mechanism.id === MECHANISM_ELECTRONIC
          : null,
        isOneTimePayment: this.filterModel.isOneTimePayment?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      const { data: results } = await _getDisbursementRequests(payload);
      this.results = results.data.map((x) => {
        return { ...x, checked: false };
      });
      this.pagination = {
        ...this.pagination,
        totalItemCount: results.totalItemCount,
        pageCount: results.pageCount,
        currentPage: results.currentPage,
        count: results.count,
      };
    },
    resetPagination() {
      this.pagination = {
        ...this.pagination,
        totalItemCount: 1,
        pageCount: 1,
        currentPage: 1,
        count: 1,
      };
    },
    async viewCertification() {
      this.validateVisitCertification = true;
      this.showAdvanced = true;

      // Delay for show advanced filter
      await new Promise((resolve) => setTimeout(resolve, 100));

      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor seleccionar el año, mes e institución para generar la Certificación de Visitas',
          'error'
        );
        return;
      }

      const requestNumberOrStudentNameOrSie = +this.filterModel
        .requestNumberOrStudentNameOrSie;

      const payload = {
        academicYearId: this.filterModel.academicYear.id,
        month: this.filterModel.month?.id,
        schoolId: this.filterModel.school?.id,
        studentSieId:
          requestNumberOrStudentNameOrSie == 0
            ? null
            : requestNumberOrStudentNameOrSie,
      };

      await _getVisitCertificationDirectPayment(payload).then((response) => {
        appServices.ConvertToBlob(response.data);
      });
    },
    openRequestReinbursement(value) {
      this.disbursementRequestType = value;
    },
    cancelPaymentProcess() {
      this.isSearchValid = false;
      this.results.forEach((item) => (item.checked = false));
    },
    savedPaymentProcess() {
      this.cancelPaymentProcess();
      this.onSearchHandler();
    },
    onChangeMonthHandler() {
      this.isSearchValid = false;
    },
    onChangeRequestStatusHandler() {
      this.isSearchValid = false;
    },
    onChangeDeterminationHandler() {
      this.isSearchValid = false;
    },
    onChangeIsOneTimePaymentHandler() {
      this.isSearchValid = false;
    },
    onChangeSchoolHandler() {
      this.isSearchValid = false;
    },
    onCheckAllRowsHandler(value) {
      if (value || this.allRowsChecked)
        this.results.forEach((item) => (item.checked = value));
    },
  },
};
</script>
<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';
.payment-process-section
  ::v-deep
  .input-group.checked
  .option-input
  + label
  span {
  background-color: map-get($colors, 'green') !important;
}
.payment-process-section
  ::v-deep
  .input-group
  .option-input:checked
  + label
  span
  .ic-check {
  background-image: url('../../../assets/img/white-check.svg');
  background-size: 70%;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
